import {
  Flex,
  Grid,
  GridItem,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import type React from 'react';
import { Flag } from '../../components';
import Button from '../../components/Button';
import { Country, type CountryCode, countriesInfo } from '@/models/country';
import { DISPLAY_POINTS_SYSTEM } from '@/constants/displayPointsSystem';
import { useMobileFormModal } from '@/hooks/useMobileFormModal';
import { HomeHeading } from './HomeHeading';

const CollectPointsSection: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const data: TeamVsProps['data'] = [
    { countryCode: 'GB_ENG', score: 3, points: 6 },
    { countryCode: 'BR', score: 2, points: 2 },
  ];
  return (
    <VStack w='full' maxW='full' m='auto' justifyContent='center'>
      <HomeHeading mb='2rem' fontWeight={700} width={{ base: '90%', md: '70%', lg: '70%', xl: '80%' }}>
        2. Collect Points When Your Teams Score Goals, Win Games, and Progress Through the Tournament
      </HomeHeading>
      <TeamVs data={data} />
      <Button
        text='Points System'
        onClick={onOpen}
        fontSize={{ base: '14px', md: '15px' }}
        px={{ base: '16px', md: '20px' }}
        py={{ base: '8px', md: '12px' }}
      />
      <PointsModal isOpen={isOpen} onClose={onClose} />
    </VStack>
  );
};

export default CollectPointsSection;

interface TeamVsProps {
  data: Array<{
    countryCode: CountryCode;
    score: number;
    points: number;
  }>;
}
const TeamVs: React.FC<TeamVsProps> = ({ data }) => {
  const [team1, team2] = data;

  return (
    <Grid gridTemplateColumns='repeat(5, 1fr)' alignItems='center' gap={0} w='100%' pos='relative'>
      <GridItem colStart={2}>
        <Team {...team1} />
      </GridItem>
      <HStack
        justifyContent={'center'}
        gap={6}
        mb={{
          base: '2em',
          md: '20%',
          lg: '25%',
        }}
        mx={4}
      >
        {[team1.score, '−', team2.score].map((text) => (
          <Text
            key={String(text)}
            textAlign='center'
            fontWeight={700}
            lineHeight={1}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
            fontSize={{
              base: '28px',
              md: '36px',
              lg: '44px',
            }}
          >
            {text}
          </Text>
        ))}
      </HStack>
      <Team {...team2} />
    </Grid>
  );
};

const Team = ({ countryCode, points }: Omit<TeamVsProps['data'][0], 'score'>) => {
  return (
    <VStack gap={0}>
      <Flag
        country={Country[countryCode]}
        w={{ base: '56px', md: '68px', lg: '80px', xl: '100px' }}
        borderRadius='4px'
      />
      <Text
        fontWeight={600}
        fontSize={{
          base: '12px',
          md: '16px',
          lg: '24px',
        }}
      >
        {countriesInfo[countryCode].name}
      </Text>
      <Text
        fontWeight={600}
        fontSize={{
          base: '12px',
          md: '16px',
          lg: '24px',
        }}
      >
        (+{points})
      </Text>
    </VStack>
  );
};

const PointsModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: VoidFunction }) => {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const { modalSize, isCentered, scrollBehavior, contentProps, footerPb } = useMobileFormModal();

  const data = DISPLAY_POINTS_SYSTEM.map(([label, points]) => {
    if (label === 'Winner') return ['Champions 🏆', points] as const;
    return [label, points] as const;
  });
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={isCentered} size={modalSize} scrollBehavior={scrollBehavior}>
      <ModalOverlay />
      <ModalContent {...contentProps}>
        <ModalHeader px={{ base: 4, md: 6 }}>
          <HStack justifyContent='space-between' pr={{ base: 0, md: '2em' }}>
            <Text fontWeight={600}>10 Teams</Text>
            <Text fontWeight={600}>Points</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton mt={2} mr={2} />
        <ModalBody px={{ base: 4, md: 6 }} pb={footerPb}>
          <VStack gap={2} borderRadius='16px' borderColor={borderColor} borderWidth='1px' px={{ base: 4, md: '26px' }} py='12px'>
            {data.map(([text, points], idx) => (
              <Flex
                key={text}
                w='full'
                justifyContent='space-between'
                borderBottomWidth={idx === data.length - 1 ? '0px' : '1px'}
                borderBottomColor={borderColor}
                py='16px'
                pb={idx === data.length - 1 ? '0px' : 'inherit'}
              >
                <Text fontWeight={600}>{text}</Text>
                <Text fontWeight={600}>{points}</Text>
              </Flex>
            ))}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
